/**
 * 인앱 카메라 컴포넌트
 *
 * getUserMedia + <video> 엘리먼트 기반으로 전체화면 카메라 UI를 제공합니다.
 * 촬영 즉시 onCapture로 base64를 전달하고, 썸네일 스트립으로 촬영 결과를 표시합니다.
 *
 * 레이아웃:
 * +----------------------------+
 * | ## 상단 바 ################# |  <- bg-black, pt-safe
 * | # [X]   [Flash]   [Flip] # |
 * +----------------------------+
 * |                            |
 * |   <video object-fit:cover> |  <- 카메라 스트림
 * |                            |
 * +----------------------------+
 * | [썸네일1] [썸네일2] [...]   |  <- 촬영된 사진 스트립
 * +----------------------------+
 * | ## 하단 바 ################# |  <- bg-black, pb-safe
 * | # [확인]  [셔터]        # |
 * +----------------------------+
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Zap, ZapOff, RefreshCw, Check } from "lucide-react";
import { useInAppCamera } from "@/hooks/useInAppCamera";
import type { FlashMode } from "@/hooks/useInAppCamera";

// 글로벌 스타일 1회 주입 (DOM 기반 체크로 HMR 중복 방지)
function injectCameraStyles() {
  if (document.getElementById("ict-camera-styles")) return;
  const style = document.createElement("style");
  style.id = "ict-camera-styles";
  style.textContent = `
    @keyframes thumbIn {
      from { opacity: 0; transform: scale(0.8); }
      to { opacity: 1; transform: scale(1); }
    }
    .scrollbar-hide::-webkit-scrollbar { display: none; }
    .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
  `;
  document.head.appendChild(style);
}

// ============================================================================
// Types
// ============================================================================

interface InAppCameraProps {
  /** 카메라 열림 상태 */
  isOpen: boolean;
  /** 촬영 결과 전달 (base64) */
  onCapture: (base64: string) => void;
  /** 카메라 닫기 */
  onClose: () => void;
  /** 최대 촬영 수 제한 (선택) */
  maxPhotos?: number;
}

// ============================================================================
// Sub Components
// ============================================================================

/** 플래시 아이콘 + 라벨 */
function FlashButton({ mode, onToggle }: { mode: FlashMode; onToggle: () => void }) {
  const icon = mode === "off" ? <ZapOff className="w-5 h-5" /> : <Zap className="w-5 h-5" />;
  const label = mode === "on" ? "ON" : null;

  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center gap-1 text-white active:opacity-60 transition-opacity"
      aria-label={`플래시 ${mode}`}
    >
      {icon}
      {label && <span className="text-xs font-medium">{label}</span>}
    </button>
  );
}

/** 셔터 버튼 - 네이티브 카메라 스타일 */
function ShutterButton({
  onPress,
  disabled,
  animating,
}: {
  onPress: () => void;
  disabled?: boolean;
  animating: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onPress}
      disabled={disabled}
      className="relative w-[72px] h-[72px] flex items-center justify-center disabled:opacity-50"
      aria-label="촬영"
    >
      <span className="absolute inset-0 rounded-full border-[3px] border-white" />
      <span
        className={`w-[60px] h-[60px] rounded-full bg-white transition-transform duration-100 ${
          animating ? "scale-[0.85]" : "scale-100"
        }`}
      />
    </button>
  );
}

/** 촬영된 사진 썸네일 스트립 */
function ThumbnailStrip({ thumbnails }: { thumbnails: string[] }) {
  const scrollRef = useRef<HTMLDivElement>(null);

  // 새 사진 추가 시 자동 스크롤
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [thumbnails.length]);

  if (thumbnails.length === 0) return null;

  return (
    <div className="shrink-0 bg-black/80 backdrop-blur-sm">
      <div
        ref={scrollRef}
        className="flex gap-2 px-4 py-3 overflow-x-auto scrollbar-hide"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {thumbnails.map((base64, index) => (
          <div
            key={`thumb-${index}`}
            className="relative shrink-0 w-14 h-14 rounded-lg overflow-hidden ring-1 ring-white/20"
            style={{ animation: "thumbIn 200ms ease-out forwards" }}
          >
            <img
              src={`data:image/jpeg;base64,${base64}`}
              alt={`촬영 ${index + 1}`}
              className="w-full h-full object-cover"
            />
            {/* 번호 뱃지 */}
            <span className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-black/70 text-white text-[10px] font-bold leading-none px-1">
              {index + 1}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function InAppCamera({ isOpen, onCapture, onClose, maxPhotos }: InAppCameraProps) {
  const viewfinderRef = useRef<HTMLDivElement>(null);

  const camera = useInAppCamera({ viewfinderRef });

  const [shutterAnimating, setShutterAnimating] = useState(false);
  const [captureFlash, setCaptureFlash] = useState(false);
  // 촬영된 사진 썸네일 (base64)
  const [thumbnails, setThumbnails] = useState<string[]>([]);

  const effectiveMax = maxPhotos ?? 5;
  const isMaxReached = camera.captureCount >= effectiveMax;
  const isSingleShot = maxPhotos === 1;
  const hasPhotos = thumbnails.length > 0;

  const prevIsOpenRef = useRef(false);

  // 글로벌 스타일 1회 주입
  useEffect(() => {
    injectCameraStyles();
  }, []);

  // isOpen 변경 시 카메라 시작/중지
  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      camera.open();
      setThumbnails([]);
    }
    if (!isOpen && prevIsOpenRef.current) {
      camera.close();
      setThumbnails([]);
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // 촬영 핸들러
  const handleCapture = useCallback(async () => {
    if (isMaxReached) return;

    setShutterAnimating(true);
    setTimeout(() => setShutterAnimating(false), 150);

    setCaptureFlash(true);
    setTimeout(() => setCaptureFlash(false), 100);

    const base64 = await camera.capture();
    if (!base64) return;

    onCapture(base64);

    // 단일 촬영이면 즉시 닫기
    if (isSingleShot) {
      onClose();
      return;
    }

    // 썸네일 추가 (최대 5개까지 표시)
    setThumbnails((prev) => [...prev.slice(-4), base64]);
  }, [camera, onCapture, onClose, isMaxReached, isSingleShot]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black flex flex-col"
      style={{ touchAction: "none", overscrollBehavior: "none", overflow: "hidden" }}
    >
      {/* 촬영 플래시 효과 */}
      {captureFlash && (
        <div className="absolute inset-0 z-20 bg-white pointer-events-none animate-[fadeOut_100ms_ease-out_forwards]" />
      )}

      {/* 상단 바 */}
      <div className="shrink-0 bg-black pt-safe">
        <div className="h-12 flex items-center justify-between px-4">
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center text-white active:opacity-60 transition-opacity"
            aria-label="닫기"
          >
            <X className="w-6 h-6" />
          </button>

          <FlashButton mode={camera.flashMode} onToggle={camera.toggleFlash} />

          <button
            type="button"
            onClick={camera.flipCamera}
            className="w-10 h-10 flex items-center justify-center text-white active:opacity-60 transition-opacity"
            aria-label="카메라 전환"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* 중앙 카메라 영역 */}
      <div
        ref={viewfinderRef}
        className="flex-1 relative overflow-hidden"
        style={{ touchAction: "none" }}
      >
        <video
          ref={camera.videoRef}
          autoPlay
          playsInline
          muted
          className={`absolute inset-0 w-full h-full object-cover ${
            camera.isCameraReady ? "opacity-100" : "opacity-0"
          }`}
          style={{ transform: `scale(${camera.zoomLevel})`, transformOrigin: "center" }}
        />

        {camera.showZoomIndicator && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10">
            <span className="bg-black/60 text-white text-sm font-medium px-3 py-1.5 rounded-full">
              {camera.zoomLevel.toFixed(1)}x
            </span>
          </div>
        )}
      </div>

      {/* 촬영된 사진 썸네일 스트립 */}
      {!isSingleShot && <ThumbnailStrip thumbnails={thumbnails} />}

      {/* 하단 바 */}
      <div className="shrink-0 bg-black pb-safe">
        <div className="py-5 px-6 flex items-center justify-between">
          {/* 확인 버튼 (사진이 있을 때만 표시) */}
          <div className="w-20 flex justify-start">
            {!isSingleShot && hasPhotos && (
              <button
                type="button"
                onClick={onClose}
                className="flex items-center gap-1.5 text-[#0A84FF] active:opacity-60 transition-opacity"
                aria-label="완료"
              >
                <Check className="w-5 h-5" />
                <span className="text-[15px] font-semibold">완료</span>
              </button>
            )}
          </div>

          {/* 셔터 버튼 */}
          <ShutterButton
            onPress={handleCapture}
            disabled={!camera.isCameraReady || isMaxReached}
            animating={shutterAnimating}
          />

          {/* 우측 빈 공간 (균형) */}
          <div className="w-20" />
        </div>
      </div>
    </div>,
    document.body
  );
}
